import { useState } from "react";
import { FranchiseBrand } from "../../interfaces/establishments";
import { getBundledLogo } from "../../utils/franchiseDetector";

type Brand = Pick<FranchiseBrand, "name" | "logoUrl" | "website">;

function brandDomain(website: string | null): string | null {
    if (!website) return null;
    try {
        return new URL(website.startsWith("http") ? website : `https://${website}`).hostname.replace(/^www\./, "");
    } catch {
        return null;
    }
}

/** Every image a brand can be drawn with, best first: bundled, stored, then Clearbit off its website. */
export function franchiseLogoCandidates(f: Brand): string[] {
    const domain = brandDomain(f.website);
    return [getBundledLogo(f.name), f.logoUrl, domain ? `https://logo.clearbit.com/${domain}` : null]
        .filter(Boolean) as string[];
}

/**
 * A franchise's mark: the bundled logo when we ship one, else the stored or Clearbit logo, else
 * the initial. Used at hero size on the fiche and at chip size wherever a brand is cited.
 */
export function FranchiseLogo({ f, size = 72, className = "" }: { f: Brand; size?: number; className?: string }) {
    // Index into the remote candidates below: a broken stored logo falls through to Clearbit,
    // and only when every source failed do we draw the initial.
    const [attempt, setAttempt] = useState(0);
    const style = { width: size, height: size };
    const radius = size >= 48 ? "rounded-2xl" : size >= 28 ? "rounded-lg" : "rounded-sm";
    const frame = `${radius} border border-border bg-surface flex-shrink-0 ${className}`;
    // A bundled image only: the detector's emoji fallback is not a logo and must not hide the
    // back-office URL or Clearbit.
    const bundled = getBundledLogo(f.name);
    if (bundled) {
        return <img src={bundled} alt="" className={`${frame} object-cover`} style={style} />;
    }

    const domain = brandDomain(f.website);
    const candidates = [f.logoUrl, domain ? `https://logo.clearbit.com/${domain}` : null].filter(Boolean) as string[];
    const src = candidates[attempt];

    if (src) {
        return (
            <img
                key={src}
                src={src}
                alt=""
                className={`${frame} object-cover`}
                style={style}
                onError={() => setAttempt((a) => a + 1)}
            />
        );
    }

    return (
        <div className={`${frame} flex items-center justify-center bg-surface-sunken`} style={style}>
            <span className="font-semibold text-ink-muted" style={{ fontSize: size * 0.4 }}>
                {f.name[0]?.toUpperCase()}
            </span>
        </div>
    );
}

/** A row of small overlapping brand marks, e.g. the brands a network's franchisees also run. */
export function BrandStack({ brands, size = 22, max = 4 }: { brands: Brand[]; size?: number; max?: number }) {
    const shown = brands.slice(0, max);
    const rest = brands.length - shown.length;
    return (
        <div className="flex items-center">
            {shown.map((b, i) => (
                <div key={b.name} title={b.name} className={i > 0 ? "-ml-1.5" : ""}>
                    <FranchiseLogo f={b} size={size} className="ring-2 ring-surface" />
                </div>
            ))}
            {rest > 0 && <span className="ml-1.5 font-mono text-xs text-ink-faint">+{rest}</span>}
        </div>
    );
}
